import { useState, useEffect, useCallback, type FormEvent } from 'react';
import {
  useUser,
  useLogout,
  useSwitchStaff,
  usePinLogin,
  useSectionCounts,
  useCards,
  type Inbox,
} from './hooks/useHatMail';
import CardQueue from './components/CardQueue';
import StatsView from './components/StatsView';
import ShadowView from './components/ShadowView';
import ActivityView from './components/ActivityView';
import Walkthrough, { STORAGE_KEY as WALKTHROUGH_KEY } from './components/Walkthrough';
import LegalPage, { type LegalPageKind } from './components/LegalPage';
import StartEmailView from './components/StartEmailView';
import { buildSupportMailto } from '../../support-link';

type Tab = 'retail' | 'shop' | 'start' | 'stats' | 'shadow' | 'activity';

function legalPageFromPath(pathname: string): LegalPageKind | null {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/oauth-app') return 'oauth';
  if (normalized === '/privacy' || normalized === '/privacy-policy') return 'privacy';
  if (normalized === '/terms' || normalized === '/terms-of-service') return 'terms';
  return null;
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function reconnectErrorLabel(error: string): string {
  switch (error) {
    case 'wrong_account':
      return 'Reconnect failed: wrong Google account.';
    case 'missing_refresh_token':
      return 'Reconnect failed: no long-term token returned.';
    case 'token_exchange_failed':
      return 'Reconnect failed: Google rejected the code.';
    case 'state_expired':
      return 'Reconnect expired.';
    default:
      return 'Reconnect failed.';
  }
}

// ── Login Screen ────────────────────────────────────────────

function LoginScreen({ error }: { error?: string }) {
  const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/api$/, '');
  const [pin, setPin] = useState('');
  const pinLogin = usePinLogin();

  const submitPin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pin.trim()) return;
    pinLogin.mutate(pin.trim());
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <h1 className="text-center text-3xl font-bold text-ghw-brown">GHW</h1>
          <p className="mt-1 text-center text-xl font-light text-gray-500">HatMail</p>
        </div>

        {(error || pinLogin.isError) && (
          <div className="mb-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error === 'not_authorized'
              ? "Your Google account isn't authorized for HatMail. Contact Local Nerds to get access."
              : pinLogin.isError
                ? 'Invalid PIN'
                : `Sign in failed: ${error?.replace(/_/g, ' ')}`}
          </div>
        )}

        <form onSubmit={submitPin} className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <label htmlFor="hatmail-pin" className="block text-sm font-semibold text-gray-900">
            Front of House PIN
          </label>
          <input
            id="hatmail-pin"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(event) => setPin(event.target.value)}
            className="mt-3 w-full rounded-lg border border-gray-200 px-4 py-3 text-center text-xl tracking-[0.3em] text-gray-900 outline-none transition focus:border-ghw-gold focus:ring-2 focus:ring-ghw-gold/20"
            aria-label="Front of House PIN"
          />
          <button
            type="submit"
            disabled={pinLogin.isPending || !pin.trim()}
            className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-ghw-brown px-4 py-3 text-sm font-semibold text-white transition hover:bg-ghw-brown/90 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ minHeight: '48px' }}
          >
            {pinLogin.isPending ? 'Opening...' : 'Open HatMail'}
          </button>
        </form>

        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Admin</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <a
          href={`${apiBase}/auth/google`}
          className="inline-flex w-full items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-6 py-3.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md active:scale-[0.98]"
          style={{ minHeight: '48px' }}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </a>

        <div className="mt-8 flex justify-center gap-5 text-xs text-gray-400">
          <a href={buildSupportMailto({ view: 'login' })} className="font-medium text-ghw-brown hover:text-ghw-brown/80">
            Support
          </a>
          <a href="/privacy" className="hover:text-gray-600">
            Privacy
          </a>
          <a href="/terms" className="hover:text-gray-600">
            Terms
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Main App ────────────────────────────────────────────────

function HatMailApp() {
  const [activeTab, setActiveTab] = useState<Tab>('retail');
  const [online, setOnline] = useState(navigator.onLine);
  const [walkthroughDone, setWalkthroughDone] = useState(
    () => localStorage.getItem(WALKTHROUGH_KEY) === 'true',
  );
  const [reconnectNotice, setReconnectNotice] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const reconnected = params.get('inbox_reconnected');
    const reconnectError = params.get('inbox_reconnect_error');
    if (reconnected === 'retail' || reconnected === 'shop') {
      return `${reconnected}@ reconnected.`;
    }
    if (reconnectError) {
      return reconnectErrorLabel(reconnectError);
    }
    return null;
  });
  const handleWalkthroughComplete = useCallback(() => setWalkthroughDone(true), []);

  const { data: user, isLoading: userLoading, isError: userError } = useUser();
  const logout = useLogout();
  const switchStaff = useSwitchStaff();
  const { data: counts } = useSectionCounts();
  const { data: retailQueue } = useCards('retail', 'smart');
  const { data: shopQueue } = useCards('shop', 'smart');

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    const adminOnlyTab = activeTab === 'stats' || activeTab === 'shadow' || activeTab === 'activity';
    if ((!user?.can_start_email && activeTab === 'start') || (!user?.is_admin && adminOnlyTab)) {
      setActiveTab('retail');
    }
  }, [activeTab, user?.can_start_email, user?.is_admin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reconnected = params.get('inbox_reconnected');
    const reconnectError = params.get('inbox_reconnect_error');
    if (reconnected === 'retail' || reconnected === 'shop') {
      setActiveTab(reconnected);
    }
    if (reconnected || reconnectError) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check for auth error in URL params
  const authError = new URLSearchParams(window.location.search).get('auth_error');

  // Loading state
  if (userLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mb-3 h-8 w-8 mx-auto animate-spin rounded-full border-2 border-gray-200 border-t-ghw-gold" />
          <p className="text-sm text-gray-400">Loading HatMail...</p>
        </div>
      </div>
    );
  }

  // Not authenticated: show login
  if (userError || !user) {
    return <LoginScreen error={authError || undefined} />;
  }

  const displayName = user.display_name?.split(' ')[0] || 'there';
  const totalHandled = (counts?.sent_today ?? 0) + (counts?.auto_sent ?? 0) + (counts?.auto_resolved ?? 0);
  const needsYou = (retailQueue?.counts?.total ?? 0) + (shopQueue?.counts?.total ?? 0);
  const urgent = (retailQueue?.counts?.urgent ?? 0) + (shopQueue?.counts?.urgent ?? 0);
  const inboxForTab: Inbox = activeTab === 'shop' ? 'shop' : 'retail';
  const tabs: { id: Tab; label: string }[] = [
    { id: 'retail', label: 'retail@' },
    { id: 'shop', label: 'shop@' },
    ...(user.can_start_email ? [{ id: 'start' as Tab, label: 'Start email' }] : []),
    ...(user.is_admin
      ? [
          { id: 'stats' as Tab, label: 'Stats' },
          { id: 'shadow' as Tab, label: 'Shadow' },
          { id: 'activity' as Tab, label: 'Activity' },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Walkthrough for first-time users */}
      {!walkthroughDone && (
        <Walkthrough
          userName={displayName}
          onComplete={handleWalkthroughComplete}
        />
      )}

      {/* Offline banner */}
      {!online && (
        <div className="bg-amber-500 px-4 py-2 text-center text-sm font-medium text-white">
          Offline, reconnecting...
        </div>
      )}

      {reconnectNotice && (
        <div className="bg-gray-900 px-4 py-2 text-center text-sm font-medium text-white">
          {reconnectNotice}
          <button
            type="button"
            onClick={() => setReconnectNotice(null)}
            className="ml-3 text-xs font-semibold uppercase text-white/70 hover:text-white"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-ghw-brown">GHW</span>
            <span className="text-lg font-light text-gray-400">/</span>
            <span className="text-lg font-semibold text-gray-800">HatMail</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={buildSupportMailto({ staffName: user.display_name, view: activeTab })}
              className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-ghw-brown transition hover:bg-gray-50"
              style={{ minHeight: '36px' }}
            >
              Support
            </a>
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.display_name}
                className="h-8 w-8 rounded-full"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ghw-cream text-sm font-bold text-ghw-brown">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <button
              onClick={() => (user.auth_method === 'pin' ? switchStaff.mutate() : logout.mutate())}
              className="hidden text-left text-xs text-gray-500 hover:text-gray-700 sm:block"
              title={user.auth_method === 'pin' ? 'Switch staff' : 'Sign out'}
            >
              <span className="block font-medium text-gray-700">{user.display_name}</span>
              <span className="block text-gray-400">
                {user.auth_method === 'pin' ? 'Switch staff' : 'Sign out'}
              </span>
            </button>
          </div>
        </div>

        {/* Status bar */}
        <div className="mx-auto max-w-2xl px-4 pb-3">
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-800">
              {greeting()}, {displayName}.
            </span>{' '}
            <span className="text-gray-400">
              {totalHandled} handled &middot;{' '}
              {needsYou} need you
              {urgent > 0 && (
                <span className="text-red-500">
                  {' '}&middot; {urgent} urgent
                </span>
              )}
            </span>
          </p>
        </div>

        {/* Tab bar */}
        <div className="mx-auto max-w-2xl px-4">
          <nav className="-mb-px flex gap-4 overflow-x-auto" aria-label="Inbox tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-ghw-gold text-ghw-brown'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
                style={{ minHeight: '44px' }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-2xl px-4 py-5">
        {activeTab === 'start' ? (
          <StartEmailView testing={user.composer_access === 'admin'} />
        ) : activeTab === 'activity' ? (
          <ActivityView />
        ) : activeTab === 'shadow' ? (
          <ShadowView />
        ) : activeTab === 'stats' ? (
          <StatsView />
        ) : (
          <CardQueue inbox={inboxForTab} currentUserId={user.id} isAdmin={user.is_admin} />
        )}
      </main>
      <footer className="mx-auto max-w-2xl px-4 pb-6 text-center text-xs text-gray-400">
        <a href="/privacy" className="hover:text-gray-600">
          Privacy
        </a>
        <span className="mx-2">|</span>
        <a href="/terms" className="hover:text-gray-600">
          Terms
        </a>
      </footer>
    </div>
  );
}

export default function App() {
  const legalPage = legalPageFromPath(window.location.pathname);
  if (legalPage) return <LegalPage page={legalPage} />;
  return <HatMailApp />;
}
